import axios from 'axios';
import { API_BASE_URL } from '../config';

export const TOKEN_STORAGE_KEY = 'token';
/** Pending email-OTP challenge JWT (not a full session). */
export const PENDING_MFA_TOKEN_KEY = 'pending_mfa_token';

/** No auth interceptors — used for POST /auth/refresh to avoid recursion. */
const refreshHttpClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 360000,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const decodeToken = (token: string): { exp?: number; [key: string]: unknown } | null => {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload) as { exp?: number; [key: string]: unknown };
  } catch (error) {
    console.warn('[Token] Failed to decode token:', error);
    return null;
  }
};

/** True when JWT is a password-only pending MFA challenge (no API access). */
export const isPendingMfaToken = (token: string): boolean => {
  const decoded = decodeToken(token);
  return decoded?.typ === 'pending_mfa';
};

/** True when token is missing exp or expires within the next 5 seconds (clock skew buffer). */
export const isTokenExpired = (token: string): boolean => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;
  return decoded.exp * 1000 < Date.now() + 5000;
};

/** Fallback TTL when JWT lacks ``iat`` (matches Backend ACCESS_TOKEN_EXPIRE_MINUTES default). */
export const DEFAULT_ACCESS_TOKEN_TTL_SECONDS = 120 * 60;

/**
 * Remaining lifetime as a fraction of total TTL (0 = expired, 1 = freshly issued).
 * Prefers ``iat``→``exp``. Does **not** use ``mfa_at`` (MFA wall time, not token issue time).
 * Falls back to {@link DEFAULT_ACCESS_TOKEN_TTL_SECONDS} when ``iat`` is absent.
 */
export const tokenRemainingLifetimeFraction = (token: string, nowMs: number = Date.now()): number | null => {
  const decoded = decodeToken(token);
  if (!decoded || typeof decoded.exp !== 'number') return null;
  const expMs = decoded.exp * 1000;
  const remainingMs = expMs - nowMs;
  if (remainingMs <= 0) return 0;

  let issuedMs: number | null = null;
  if (typeof decoded.iat === 'number') {
    issuedMs = decoded.iat * 1000;
  }

  const ttlMs =
    issuedMs != null && expMs > issuedMs
      ? expMs - issuedMs
      : DEFAULT_ACCESS_TOKEN_TTL_SECONDS * 1000;
  if (ttlMs <= 0) return null;
  return Math.min(1, Math.max(0, remainingMs / ttlMs));
};

/**
 * Proactive refresh window: remaining TTL is in (0, 25%] of total lifetime.
 * Call on authenticated API usage so idle wall-clock expiry is extended while the operator is active.
 */
export const shouldProactivelyRefreshToken = (
  token: string,
  nowMs: number = Date.now()
): boolean => {
  if (isPendingMfaToken(token) || isTokenExpired(token)) return false;
  const fraction = tokenRemainingLifetimeFraction(token, nowMs);
  if (fraction == null) return false;
  return fraction > 0 && fraction <= 0.25;
};

export const getStoredAccessToken = (): string | null => localStorage.getItem(TOKEN_STORAGE_KEY);

export const clearStoredAccessToken = (): void => {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
};

export const getPendingMfaToken = (): string | null => sessionStorage.getItem(PENDING_MFA_TOKEN_KEY);

export const setPendingMfaToken = (token: string): void => {
  sessionStorage.setItem(PENDING_MFA_TOKEN_KEY, token);
};

export const clearPendingMfaToken = (): void => {
  sessionStorage.removeItem(PENDING_MFA_TOKEN_KEY);
};

/** Read mfa_step from a pending_mfa JWT (enroll vs challenge copy). */
export const pendingMfaStepFromToken = (
  token: string | null = getPendingMfaToken()
): 'enroll' | 'challenge' | null => {
  if (!token) return null;
  const decoded = decodeToken(token);
  if (decoded?.typ && decoded.typ !== 'pending_mfa') return null;
  // Opaque / undecodable stored token still means MFA is in progress.
  if (!decoded) return 'challenge';
  return decoded.mfa_step === 'enroll' ? 'enroll' : 'challenge';
};

/**
 * Auth routes that must not use localStorage access-token refresh.
 * MFA verify/resend send an explicit pending_mfa Bearer; skip overwrite/401-refresh.
 */
export const isUnauthenticatedAuthRoute = (url: string): boolean =>
  /\/auth\/(login|register)(?:\?|$)/.test(url) ||
  url.includes('/auth/forgot-password') ||
  url.includes('/auth/reset-password') ||
  url.includes('/auth/refresh') ||
  url.includes('/auth/mfa/') ||
  url.includes('/dev/');

export const isPublicEndpoint = (url: string): boolean => {
  const baseUrl = url.split('?')[0] || '';
  return (
    baseUrl === '/properties' ||
    (baseUrl.startsWith('/properties') && !baseUrl.includes('/properties/validate')) ||
    baseUrl.includes('/option-catalogs')
  );
};

let refreshPromise: Promise<boolean> | null = null;

/**
 * Refresh the access token. On failure the stored token is left in place so callers
 * can decide (expired path clears; proactive path keeps a still-valid JWT).
 * pending_mfa tokens are always cleared — they must not become a session.
 */
const attemptRefresh = async (): Promise<boolean> => {
  const accessToken = getStoredAccessToken();
  if (!accessToken) {
    return false;
  }
  if (isPendingMfaToken(accessToken)) {
    clearStoredAccessToken();
    return false;
  }
  try {
    const response = await refreshHttpClient.post<{ access_token: string }>('/auth/refresh', {
      access_token: accessToken,
    });
    if (response.data?.access_token) {
      localStorage.setItem(TOKEN_STORAGE_KEY, response.data.access_token);
      return true;
    }
  } catch {
    // Leave stored access token; caller decides whether to clear.
  }
  return false;
};

/** Deduplicated refresh — concurrent callers share one in-flight request. */
export const refreshAccessToken = async (): Promise<boolean> => {
  if (!refreshPromise) {
    refreshPromise = attemptRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};
